import { useEffect, useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { adminApi, apiError } from "@/components/admin/api";
import PanelOverview from "@/components/admin/PanelOverview";
import AgendaView from "@/components/admin/AgendaView";
import BookingsView from "@/components/admin/BookingsView";
import AvailabilityView from "@/components/admin/AvailabilityView";
import ServicesView from "@/components/admin/ServicesView";
import PackagesView from "@/components/admin/PackagesView";
import { LogOut } from "lucide-react";

const TABS = [
  { id: "painel", label: "Painel" },
  { id: "agenda", label: "Agenda" },
  { id: "agendamentos", label: "Agendamentos" },
  { id: "pacotes", label: "Pacotes" },
  { id: "servicos", label: "Serviços" },
  { id: "disponibilidade", label: "Disponibilidade" },
];

const Admin = () => {
  const [auth, setAuth] = useState(null);
  const [tab, setTab] = useState("painel");
  const [packageFocus, setPackageFocus] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const openPackage = (code) => {
    setPackageFocus(code);
    setTab("pacotes");
  };

  useEffect(() => {
    adminApi
      .get("/auth/me")
      .then((res) => setAuth(res.data?.role === "admin" ? res.data : false))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    const interceptor = adminApi.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && auth) setAuth(false);
        return Promise.reject(err);
      }
    );
    return () => adminApi.interceptors.response.eject(interceptor);
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await adminApi.post("/auth/login", { email: email.trim(), password });
      if (res.data?.role !== "admin") {
        toast.error("Acesso restrito ao administrador.");
        await adminApi.post("/auth/logout").catch(() => {});
        return;
      }
      setAuth(res.data);
      toast.success(`Bem-vinda, ${res.data.name.split(" ")[0]}.`);
    } catch (err) {
      toast.error(apiError(err, "Não foi possível entrar. Verifique e-mail e senha."));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    setAuth(false);
    toast.success("Sessão encerrada.");
  };

  if (auth === null) {
    return (
      <section className="container-x pt-32" data-testid="admin-loading">
        <div className="mx-auto h-56 max-w-md animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" />
      </section>
    );
  }

  if (auth === false) {
    return (
      <>
        <SEO title="Área administrativa | Sabrina Mota" description="Acesso restrito." path="/admin" />
        <section className="container-x flex min-h-[80vh] items-center justify-center pt-24 pb-16" data-testid="admin-login-page">
          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="w-full max-w-md rounded-3xl border border-raiz-900/10 bg-cream p-8 sm:p-10"
          >
            <img src="/assets/tree-mark.png" alt="" className="h-10 w-auto" width="40" height="40" />
            <h1 className="mt-6 font-serif text-3xl font-light text-ink">Área da Sabrina</h1>
            <p className="mt-2 text-sm text-ink/60">Acesso restrito. Entre com seu e-mail e senha.</p>
            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="admin-email" className="mb-2 block text-sm font-medium text-ink">E-mail</label>
                <input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full rounded-2xl border border-raiz-800/20 bg-paper px-5 py-3.5 text-sm text-ink focus:border-raiz-700 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-2 block text-sm font-medium text-ink">Senha</label>
                <input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full rounded-2xl border border-raiz-800/20 bg-paper px-5 py-3.5 text-sm text-ink focus:border-raiz-700 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                data-testid="admin-login-button"
                disabled={loggingIn}
                className="w-full rounded-full bg-raiz-800 py-4 text-sm font-medium tracking-wide text-paper transition-colors hover:bg-raiz-700 disabled:opacity-60"
              >
                {loggingIn ? "Entrando…" : "Entrar"}
              </button>
            </div>
          </form>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title="Painel administrativo | Sabrina Mota" description="Área restrita." path="/admin" />
      <section className="pt-24 lg:pt-28" data-testid="admin-panel">
        <div className="container-x pb-20">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-raiz-900/10 pb-6">
            <div>
              <p className="eyebrow">Área restrita</p>
              <h1 className="mt-3 font-serif text-3xl font-light text-ink sm:text-4xl" data-testid="admin-title">
                Painel da <em className="italic text-raiz-700">Sabrina</em>
              </h1>
            </div>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-raiz-800/25 px-5 py-2.5 text-xs font-medium text-raiz-800 transition-colors hover:border-raiz-800"
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
              Sair
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.id ? "border-raiz-800 bg-raiz-800 text-paper" : "border-raiz-800/20 text-raiz-900 hover:border-raiz-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-10">
            {tab === "painel" && <PanelOverview onOpenPackage={openPackage} />}
            {tab === "agenda" && <AgendaView />}
            {tab === "agendamentos" && <BookingsView onOpenPackage={openPackage} />}
            {tab === "pacotes" && <PackagesView focusCode={packageFocus} onClearFocus={() => setPackageFocus(null)} />}
            {tab === "servicos" && <ServicesView />}
            {tab === "disponibilidade" && <AvailabilityView />}
          </div>
        </div>
      </section>
    </>
  );
};

export default Admin;
